import { useAuth, type Agent, type Note, formatIstTime } from '@wa/shared';

/**
 * A note in the thread.
 *
 * Deliberately unlike every message bubble on the screen: centred rather than
 * sided, amber rather than white or green, and labelled. Someone scrolling a
 * thread must never have to read carefully to work out whether the customer saw
 * a line — the answer has to be obvious at a glance.
 */
export function NoteBubble({ note, agents, onDelete }: {
  note: Note;
  agents: Agent[];
  onDelete?: () => void;
}) {
  const { user } = useAuth();
  const mine = user?.id === note.agentId;

  const mentioned = note.mentionedAgentIds
    .map((id) => agents.find((a) => a.id === id)?.displayName)
    .filter((name): name is string => Boolean(name));

  const mentionsMe = user ? note.mentionedAgentIds.includes(user.id) : false;

  return (
    <div className="flex justify-center px-2 py-1">
      <div
        className={`max-w-[min(34rem,85%)] rounded-lg border px-3 py-2 text-small leading-snug ${
          mentionsMe
            ? 'border-(--color-note-strong) bg-(--color-note) shadow-sm'
            : 'border-(--color-note-line) bg-(--color-note)'
        }`}
      >
        <p className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-micro tracking-wide uppercase">
          <span className="font-semibold text-(--color-note-ink)">Internal note</span>
          <span className="text-(--color-note-ink) opacity-70 normal-case">
            {note.agentName ?? 'Someone'}
          </span>
          {mentionsMe && (
            <span className="rounded bg-(--color-note-strong) px-1.5 py-px text-micro font-semibold text-(--color-note)">
              mentions you
            </span>
          )}

          {mine && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="tap ml-auto rounded px-1 text-(--color-note-ink) normal-case opacity-60 hover:opacity-100"
              aria-label="Delete this note"
            >
              <svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true">
                <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </p>

        <p className="break-words whitespace-pre-wrap text-(--color-note-ink)">{note.body}</p>

        <p className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-micro text-(--color-note-ink) opacity-70">
          <span className="min-w-0 truncate">
            {mentioned.length > 0 && `Notified ${mentioned.join(', ')}`}
          </span>
          <span className="shrink-0 tabular-nums">
            {formatIstTime(note.createdAt)}
          </span>
        </p>
      </div>
    </div>
  );
}
